"use client";

import { memo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronRight } from "lucide-react";
import { CodeBlock } from "./CodeBlock";

interface CellPreview {
  purpose: string;
  content: string;
  status: string;
  stdout?: string;
}

interface SubNotebookProgress {
  status: string;
  cell_count?: number;
  name?: string;
  query?: string;
  index: number;
  cells?: CellPreview[];
}

interface FundAgentProgressCardProps {
  subNotebooks: Record<string, SubNotebookProgress>;
}

function StatusDot({ status, size = "sm" }: { status: string; size?: "sm" | "md" }) {
  const sizeClass = size === "md" ? "w-2 h-2" : "w-1.5 h-1.5";
  
  if (status === "success") {
    return <div className={`${sizeClass} rounded-full bg-foreground`} />;
  }
  if (status === "executing") {
    return (
      <div className={`relative ${sizeClass}`}>
        <div className={`absolute inset-0 rounded-full bg-foreground animate-ping opacity-60`} />
        <div className={`relative ${sizeClass} rounded-full bg-foreground`} />
      </div>
    );
  }
  if (status === "error") {
    return <div className={`${sizeClass} rounded-full bg-red-400`} />;
  }
  if (status === "canceled") {
    return <div className={`${sizeClass} rounded-full bg-yellow-400`} />;
  }
  return <div className={`${sizeClass} rounded-full bg-foreground/30`} />;
}

function CellRow({ cell, isLast, isLastCell }: { cell: CellPreview; isLast: boolean; isLastCell: boolean }) {
  const [manualOverride, setManualOverride] = useState<boolean | null>(null);
  const expanded = manualOverride !== null ? manualOverride : isLastCell;
  const purposeText = cell.purpose || "Processing...";
  
  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="relative pl-5"
    >
      {/* Tree lines */}
      <div className={`absolute left-[7px] top-0 w-px bg-foreground/15 ${isLast && !expanded ? "h-[14px]" : "h-full"}`} />
      <div className="absolute left-[7px] top-[14px] w-2.5 h-px bg-foreground/15" />

      {/* Cell header - clickable */}
      <button
        onClick={() => setManualOverride(expanded ? false : true)}
        className="flex items-center gap-2 pl-3 py-1.5 w-full text-left hover:bg-foreground/5 rounded transition-colors"
      >
        <ChevronRight className={`w-3 h-3 text-foreground/40 transition-transform ${expanded ? "rotate-90" : ""}`} />
        <StatusDot status={cell.status} />
        <span className={`text-xs flex-1 ${
          cell.status === "executing" ? "text-foreground" : "text-foreground/70"
        }`}>
          {purposeText}
        </span>
      </button>

      {/* Expanded content: code + stdout */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="ml-6 mt-1 mb-2 overflow-hidden space-y-2"
          >
            {cell.content && <CodeBlock code={cell.content} language="python" />}
            {cell.stdout && (
              <pre className="text-xs text-foreground/60 bg-foreground/5 rounded px-3 py-2 whitespace-pre-wrap font-mono">
                {cell.stdout}
              </pre>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function QueryRow({ 
  subId, 
  info, 
  index 
}: { 
  subId: string; 
  info: SubNotebookProgress; 
  index: number;
}) {
  const queryText = info.query || info.name || `Query ${index + 1}`;
  const cells = info.cells || [];
  
  return (
    <motion.div
      layout
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }}
      className="py-1"
    >
      {/* Query Header */}
      <div className="flex items-center gap-2.5 py-1.5">
        <StatusDot status={info.status} size="md" />
        <span className={`text-sm font-medium flex-1 ${
          info.status === "executing" ? "text-foreground" : "text-foreground/80"
        }`}>
          {queryText}
        </span>
        {info.cell_count !== undefined && info.cell_count > 0 && (
          <span className="text-xs text-foreground/40">{info.cell_count}</span>
        )}
      </div>

      {/* Cells List */}
      <AnimatePresence>
        {cells.length > 0 && (
          <motion.div className="flex flex-col mt-1">
            {cells.map((cell, idx) => (
              <CellRow 
                key={`${subId}-cell-${idx}`} 
                cell={cell} 
                isLast={idx === cells.length - 1}
                isLastCell={idx === cells.length - 1}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function FundAgentProgressCardComponent({ subNotebooks }: FundAgentProgressCardProps) {
  const entries = Object.entries(subNotebooks).sort(
    ([, a], [, b]) => a.index - b.index
  );

  if (entries.length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="w-full"
    >
      {/* Content List - no header, just queries */}
      <div className="space-y-2">
        <AnimatePresence mode="popLayout">
          {entries.map(([subId, info], idx) => (
            <QueryRow
              key={subId}
              subId={subId}
              info={info}
              index={idx}
            />
          ))}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}

export const FundAgentProgressCard = memo(FundAgentProgressCardComponent);
